/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Game } from 'types/entities/Game'
import { Timeslot } from 'types/entities/Timeslot'
import { TimeslotViewMode } from 'types/enums/TimeslotViewMode'
import { CalendarTab } from 'types/enums/CalendarTab'
import { CalendarView } from 'types/enums/CalendarView'
import { ReservationPlan } from 'types/entities/ReservationPlan'
import { ReservationCheckoutPlan } from 'types/entities/ReservationCheckoutPlan'
import type { AnyRace } from 'types/entities/AnyRace'
import type { Reservation } from 'types/entities/Reservation'
import type { ShoppingCartItem } from 'types/entities/ShoppingCartItem'
import type { ReservationTodo } from 'types/entities/ReservationTodo'
import type { PackagesCart } from 'types/entities/PackagesCart'
import type { SetReservationPackageCartItems } from 'types/entities/SetReservationPackageCartItems'
import type { SetExtraInvoiceNoteToReservation } from 'types/entities/SetExtraInvoiceNoteToReservation'
import type { MonthDataResponse } from 'types/responses/MonthDataResponse'
import type { CalendarViewDay } from 'types/entities/specific/Calendar/CalendarViewDay'
import type { PreparedGroup } from 'types/entities/PreparedGroup'
import type { Tickets } from 'types/entities/specific/Calendar/CalendarViewDay'
import { CalendarMonthTagType } from 'types/enums/CalendarMonthTagType'
import type { Offer } from 'types/entities/Offer'
import type {Reminder} from 'types/entities/Reminder'
import {reconcileCalendarReminders, type CalendarReminderScope, type ConfirmedCalendarReminder} from 'utils/reminders/reconcileCalendarReminders'

type CartTotals = Pick<PackagesCart, 'id' | 'drivers_count_adult' | 'drivers_count_child' | 'price' | 'prices'>
interface ConfirmedOfferCart {
    revision: number
    cart: CartTotals
}

const cartTotals = (cart: CartTotals): CartTotals => ({
    id: cart.id,
    drivers_count_adult: cart.drivers_count_adult,
    drivers_count_child: cart.drivers_count_child,
    price: cart.price,
    prices: cart.prices,
})

// Only totals of the same cart are authoritative here. Keep calendar-specific
// relationships, dates and reservation state from the calendar response.
const applyConfirmedCarts = (month: MonthDataResponse | null, confirmed: Record<number, ConfirmedOfferCart>) => {
    if (!month) return null
    const apply = (offer: Offer) => {
        const cart = confirmed[offer.id]?.cart
        if (!cart) return offer
        return {
            ...offer,
            packagesCart: offer.packagesCart?.id === cart.id ? {...offer.packagesCart, ...cart} : offer.packagesCart,
            calculations: offer.calculations?.map(calculation => calculation.packagesCart?.id === cart.id
                ? {...calculation, packagesCart: {...calculation.packagesCart, ...cart}} : calculation),
        }
    }
    return {...month, offers: month.offers?.map(apply), days: month.days.map(day => ({...day, offers: day.offers?.map(apply)}))}
}

export interface CalendarParams {
    view: string
    day?: string
    month: string
    year: string
}

export interface CalendarPreparedGroupResponse {
    preparedGroup: PreparedGroup | null
    races: AnyRace[]
    games: Game[]
}

interface CalendarState {
    confirmedReminders: Record<number, ConfirmedCalendarReminder>
    reminderScope: CalendarReminderScope | null
    confirmedOfferCarts: Record<number, ConfirmedOfferCart>
    day: CalendarViewDay | null
    week: CalendarViewDay | null
    month: MonthDataResponse | null
    preparedGroupResponse: CalendarPreparedGroupResponse
    reservationSelectedTimeslots: number[]
    selectedTimeslots: number[]
    timeslotViewMode: TimeslotViewMode
    calendarTab: CalendarTab
    selectedReservation: Reservation | null
    reservationPlans: ReservationPlan[] | []
    params: CalendarParams
    monthFilters: CalendarMonthTagType[]
}

const initialState: CalendarState = {
    confirmedReminders: {},
    reminderScope: null,
    confirmedOfferCarts: {},
    day: null,
    week: null,
    month: null,
    preparedGroupResponse: {
        preparedGroup: null,
        races: [],
        games: [],
    },
    timeslotViewMode: TimeslotViewMode.Editing,
    calendarTab: CalendarTab.Reservation,
    reservationSelectedTimeslots: [],
    selectedTimeslots: [],
    selectedReservation: null,
    reservationPlans: [],
    params: {
        view: CalendarView.Month,
        month: '',
        year: '',
        day: '',
    },
    monthFilters: [
        CalendarMonthTagType.Standard,
        CalendarMonthTagType.Offers,
        CalendarMonthTagType.Leads,
        CalendarMonthTagType.Reservations,
    ],
}

const calendarSlice = createSlice({
    name: 'calendar',
    initialState,
    reducers: {
        setParams(state, action: PayloadAction<CalendarParams>) {
            state.params = action.payload
        },
        setDayData(state, action: PayloadAction<CalendarViewDay | null>) {
            state.day = action.payload
        },
        setDayTicketData(state, action: PayloadAction<Tickets>) {
            if (state.day) {
                state.day.tickets = action.payload
            }
        },
        setWeekData(state, action: PayloadAction<CalendarViewDay | null>) {
            state.week = action.payload
        },
        setMonthData(state, action: PayloadAction<MonthDataResponse>) {
            state.month = reconcileCalendarReminders(applyConfirmedCarts(action.payload, state.confirmedOfferCarts), state.confirmedReminders, state.reminderScope)
        },
        setCalendarReminderScope(state, action: PayloadAction<CalendarReminderScope>) {
            state.reminderScope = action.payload
        },
        confirmCalendarReminder(state, action: PayloadAction<Reminder>) {
            const reminder = action.payload
            state.confirmedReminders[reminder.id] = {reminder, revision: (state.confirmedReminders[reminder.id]?.revision ?? 0) + 1}
            state.month = reconcileCalendarReminders(state.month, state.confirmedReminders, state.reminderScope)
        },
        beginReminderRevalidation(state, action: PayloadAction<number>) {
            const entry = state.confirmedReminders[action.payload]
            if (entry) entry.revision += 1
        },
        removeCalendarReminder(state, action: PayloadAction<number>) {
            const id = action.payload
            const previous = state.confirmedReminders[id]
            const reminder = previous?.reminder ?? state.month?.days.flatMap(day => day.reminders ?? []).find(item => item.id === id)
            if (!reminder) return
            state.confirmedReminders[id] = {reminder, revision: (previous?.revision ?? 0) + 1, deleted: true}
            state.month = reconcileCalendarReminders(state.month, state.confirmedReminders, state.reminderScope)
        },
        revalidateCalendarReminder(state, action: PayloadAction<{reminder: Reminder; revision: number}>) {
            const {reminder, revision} = action.payload
            const entry = state.confirmedReminders[reminder.id]
            if (!entry || entry.deleted || entry.revision !== revision) return
            entry.reminder = reminder
            state.month = reconcileCalendarReminders(state.month, state.confirmedReminders, state.reminderScope)
        },
        confirmOfferCart(state, action: PayloadAction<{offerId: number; cart: CartTotals}>) {
            const {offerId, cart} = action.payload
            state.confirmedOfferCarts[offerId] = {
                revision: (state.confirmedOfferCarts[offerId]?.revision ?? 0) + 1,
                cart: cartTotals(cart),
            }
            state.month = applyConfirmedCarts(state.month, state.confirmedOfferCarts)
        },
        beginOfferCartRevalidation(state, action: PayloadAction<number>) {
            const confirmed = state.confirmedOfferCarts[action.payload]
            if (confirmed) confirmed.revision += 1
        },
        revalidateOfferCart(state, action: PayloadAction<{offerId: number; revision: number; cart: CartTotals}>) {
            const {offerId, revision, cart} = action.payload
            const confirmed = state.confirmedOfferCarts[offerId]
            // A detail request started before another save must not undo it.
            if (!confirmed || confirmed.revision !== revision) return
            confirmed.cart = cartTotals(cart)
            state.month = applyConfirmedCarts(state.month, state.confirmedOfferCarts)
        },
        setSelectedReservation(state, action: PayloadAction<Reservation | null>) {
            state.selectedReservation = action.payload
        },
        updateSelectedReservationShoppingCartItems(
            state,
            action: PayloadAction<ShoppingCartItem[]>
        ) {
            if (state.selectedReservation) {
                state.selectedReservation.shoppingCartItems = action.payload
            }
        },
        addSelectedReservationShoppingCartItem(
            state,
            action: PayloadAction<ShoppingCartItem>
        ) {
            if (
                !state.selectedReservation ||
                !state.selectedReservation.shoppingCartItems
            ) {
                return
            }
            const index = state.selectedReservation.shoppingCartItems.findIndex(
                (item) => item.guid === action.payload.guid
            )
            if (index !== -1) {
                state.selectedReservation.shoppingCartItems[index] = action.payload
            } else {
                state.selectedReservation.shoppingCartItems.push(action.payload)
            }
        },
        removeVoucherFromReservation(
            state,
            action: PayloadAction<{ voucher_id: number }>
        ) {
            if (
                !state.selectedReservation ||
                !state.selectedReservation.packagesCart
            ) {
                return
            }
            const vouchers =
                state.selectedReservation.packagesCart.packagesCartItemVouchers
            if (vouchers) {
                state.selectedReservation.packagesCart.packagesCartItemVouchers =
                    vouchers.filter(
                        (item) => item.voucher_id !== action.payload.voucher_id
                    )
            }
        },
        setTimeslotRow(state, action: PayloadAction<Timeslot>) {
            if (!state.day || !state.day.timeslots) {
                return
            }
            const index = state.day.timeslots.findIndex(
                (timeslot) => timeslot.id === action.payload.id
            )
            if (index !== -1) {
                state.day.timeslots[index] = {
                    ...state.day.timeslots[index],
                    ...action.payload,
                }
            }
        },
        setTimeslotReservationSelect(state, action: PayloadAction<Timeslot>) {
            const index = state.reservationSelectedTimeslots.indexOf(
                action.payload.id
            )
            if (index === -1) {
                state.reservationSelectedTimeslots.push(action.payload.id)
            } else {
                state.reservationSelectedTimeslots.splice(index, 1)
            }
        },
        resetTimeslotReservationSelect(state) {
            state.reservationSelectedTimeslots = []
        },
        setSelectedReservationTimeslots(state, action: PayloadAction<number[]>) {
            state.selectedTimeslots = action.payload
        },
        resetSelectedReservationTimeslots(state) {
            state.selectedTimeslots = []
        },
        setTimeslotViewMode(state, action: PayloadAction<TimeslotViewMode>) {
            state.timeslotViewMode = action.payload
        },
        setCalendarTab(state, action: PayloadAction<CalendarTab>) {
            state.calendarTab = action.payload
        },
        pushReservation(state, action: PayloadAction<Reservation>) {
            if (!state.day) {
                return
            }
            const index = state.day.reservations.findIndex(
                (reservation) => reservation.id === action.payload.id
            )
            if (index !== -1) {
                state.day.reservations[index] = action.payload
            } else {
                state.day.reservations.push(action.payload)
            }
        },
        setReservationShoppingCartItemRow(
            state,
            action: PayloadAction<ShoppingCartItem>
        ) {
            if (!state.selectedReservation?.shoppingCartItems) {
                return
            }
            const index = state.selectedReservation.shoppingCartItems.findIndex(
                (item) => item.guid === action.payload.guid
            )
            if (index === -1) {
                state.selectedReservation.shoppingCartItems.push(action.payload)
            } else {
                state.selectedReservation.shoppingCartItems[index] = action.payload
            }
        },
        setReservationTodo(state, action: PayloadAction<ReservationTodo>) {
            if (!state.selectedReservation) {
                return
            }
            const index = state.selectedReservation.reservationTodos.findIndex(
                (item) => item.todo_id === action.payload.todo_id
            )
            if (index === -1) {
                state.selectedReservation.reservationTodos.push(action.payload)
            } else {
                state.selectedReservation.reservationTodos[index] = action.payload
            }
        },
        setReservationPlans(state, action: PayloadAction<ReservationPlan[]>) {
            if (state.selectedReservation) {
                state.selectedReservation.extra = {
                    ...state.selectedReservation.extra,
                    reservation_plan: action.payload,
                }
            }
        },
        setReservationCheckoutPlans(
            state,
            action: PayloadAction<ReservationCheckoutPlan[]>
        ) {
            if (state.selectedReservation) {
                state.selectedReservation.extra = {
                    ...state.selectedReservation.extra,
                    reservation_checkout_plan: action.payload,
                }
            }
        },
        setSelectedReservationPackageCartItems(
            state,
            action: PayloadAction<SetReservationPackageCartItems>
        ) {
            if (state.selectedReservation?.packagesCart) {
                state.selectedReservation.packagesCart.packagesCartItems =
                    action.payload.packageCartItems
            }
        },
        setSelectedReservationPackageCart(
            state,
            action: PayloadAction<PackagesCart>
        ) {
            if (state.selectedReservation) {
                state.selectedReservation.packagesCart = action.payload
            }
        },
        updateSelectedReservationPackageCartPrice(
            state,
            action: PayloadAction<number>
        ) {
            if (state.selectedReservation?.packagesCart) {
                state.selectedReservation.packagesCart.price = action.payload
            }
        },
        setExtraInvoiceNoteToSelectedReservation(
            state,
            action: PayloadAction<SetExtraInvoiceNoteToReservation>
        ) {
            if (state.selectedReservation) {
                state.selectedReservation.extra = {
                    ...state.selectedReservation.extra,
                    invoice_note: action.payload.invoice_note,
                }
            }
        },
        setPreparedGroupResponse(
            state,
            action: PayloadAction<CalendarPreparedGroupResponse>
        ) {
            state.preparedGroupResponse = action.payload
        },
        resetPreparedGroupResponse(state) {
            state.preparedGroupResponse = initialState.preparedGroupResponse
        },
        toggleMonthFilter(state, action: PayloadAction<CalendarMonthTagType>) {
            if (!state.monthFilters) {
                state.monthFilters = []
            }
            const index = state.monthFilters.indexOf(action.payload)
            if (index === -1) {
                state.monthFilters.push(action.payload)
            } else {
                state.monthFilters.splice(index, 1)
            }
        },
        resetState: () => initialState,
    },
})

export const {
    setCalendarReminderScope,
    confirmCalendarReminder,
    beginReminderRevalidation,
    removeCalendarReminder,
    revalidateCalendarReminder,
    confirmOfferCart,
    beginOfferCartRevalidation,
    revalidateOfferCart,
    setParams,
    setDayData,
    setDayTicketData,
    setWeekData,
    setMonthData,
    setSelectedReservation,
    updateSelectedReservationShoppingCartItems,
    addSelectedReservationShoppingCartItem,
    removeVoucherFromReservation,
    setTimeslotRow,
    setTimeslotReservationSelect,
    resetTimeslotReservationSelect,
    setSelectedReservationTimeslots,
    resetSelectedReservationTimeslots,
    setTimeslotViewMode,
    setCalendarTab,
    pushReservation,
    setReservationShoppingCartItemRow,
    setReservationTodo,
    setReservationPlans,
    setReservationCheckoutPlans,
    setSelectedReservationPackageCartItems,
    setSelectedReservationPackageCart,
    updateSelectedReservationPackageCartPrice,
    setExtraInvoiceNoteToSelectedReservation,
    setPreparedGroupResponse,
    resetPreparedGroupResponse,
    toggleMonthFilter,
    resetState,
} = calendarSlice.actions

export default calendarSlice.reducer
